import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faChevronLeft, faChevronRight } from '@/fontawesome';
import { APIRoutes } from '@/services/api.routes';
import { formatCurrencyCentValue } from '@/utils/helpers';
import type { JournalCalendarDay } from '@/models';
import Button from '@/components/ui/Button';
import Card from '@/components/ui/Card';
import ErrorState from '@/components/ui/ErrorState';
import { pnlColorClass } from '@/components/journal/journalHelpers';
import { useJournalResource } from '@/components/journal/useJournalResource';

const MONTH_NAMES = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

function pad(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

export default function JournalGainsCalendar() {
  const now = new Date();
  const [viewYear, setViewYear] = useState(now.getFullYear());
  const [viewMonth, setViewMonth] = useState(now.getMonth()); // 0-11
  const { data: dayMapData, loading, error, reload } = useJournalResource<Record<string, JournalCalendarDay>>(
    async () => {
      const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
      const from = `${viewYear}-${pad(viewMonth + 1)}-01`;
      const to = `${viewYear}-${pad(viewMonth + 1)}-${pad(daysInMonth)}`;
      const response = await APIRoutes.getJournalCalendar(from, to);
      const map: Record<string, JournalCalendarDay> = {};
      for (const d of response.days) {
        map[d.date] = d;
      }
      return map;
    },
    [viewYear, viewMonth],
    'Fehler beim Laden des Kalenders',
  );
  const dayMap = dayMapData ?? {};

  const goPrev = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(viewYear - 1); } else { setViewMonth(viewMonth - 1); }
  };
  const goNext = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(viewYear + 1); } else { setViewMonth(viewMonth + 1); }
  };

  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const firstWeekday = (new Date(viewYear, viewMonth, 1).getDay() + 6) % 7; // Mon=0
  const cells: (number | null)[] = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const monthTotal = Object.values(dayMap).reduce((sum, d) => sum + d.totalGain, 0);

  // Darker than pnlColorClass - the amount sits on the tinted cell, not on the card.
  const cellTextColor = (gain: number): string => {
    if (gain > 0) return 'text-green-800 dark:text-green-300';
    if (gain < 0) return 'text-red-800 dark:text-red-300';
    return 'text-gray-600 dark:text-gray-300';
  };

  const cellColor = (gain: number | undefined): string => {
    // Not dark:bg-gray-800 - that is the Card's own surface, which makes the cell disappear.
    if (gain === undefined || gain === 0) return 'bg-gray-50 dark:bg-gray-700/40 border-gray-200 dark:border-gray-600';
    if (gain > 0) return 'bg-green-100 dark:bg-green-900/40 border-green-200 dark:border-green-800';
    return 'bg-red-100 dark:bg-red-900/40 border-red-200 dark:border-red-800';
  };

  return (
    <div className="space-y-4">
      <Card padded>
        <div className="flex items-center justify-between mb-4">
          <Button variant="ghost" size="sm" icon={faChevronLeft} onClick={goPrev} title="Vorheriger Monat" aria-label="Vorheriger Monat" />
          <div className="text-center">
            <div className="text-lg font-semibold text-gray-900 dark:text-white">{MONTH_NAMES[viewMonth]} {viewYear}</div>
            <div className={`text-sm font-medium ${loading ? 'text-gray-500 dark:text-gray-400' : pnlColorClass(monthTotal)}`}>
              {loading ? '…' : formatCurrencyCentValue(monthTotal, 'USD')}
            </div>
          </div>
          <Button variant="ghost" size="sm" icon={faChevronRight} onClick={goNext} title="Nächster Monat" aria-label="Nächster Monat" />
        </div>

        {loading ? (
          <div className="text-center py-16" role="status" aria-label="Lädt">
            <FontAwesomeIcon icon={faSpinner} className="w-6 h-6 text-gray-400 animate-spin" />
          </div>
        ) : error !== null ? (
          <ErrorState message={error} onRetry={reload} />
        ) : (
          <>
            <div className="grid grid-cols-7 gap-1 mb-1">
              {WEEKDAYS.map((w) => (
                <div key={w} className="text-center text-xs font-medium text-gray-500 dark:text-gray-400 py-1">{w}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {cells.map((day, idx) => {
                if (day === null) return <div key={`empty-${idx}`} />;
                const key = `${viewYear}-${pad(viewMonth + 1)}-${pad(day)}`;
                const entry = dayMap[key];
                return (
                  <div
                    key={key}
                    className={`aspect-square rounded-lg border p-1 flex flex-col ${cellColor(entry?.totalGain)}`}
                    title={entry !== undefined ? `${entry.tradeCount} Trade(s)` : undefined}
                    aria-label={entry !== undefined ? `${day}. ${MONTH_NAMES[viewMonth]}: ${formatCurrencyCentValue(entry.totalGain, 'USD')}, ${entry.tradeCount} Trades` : undefined}
                  >
                    <div className="text-xs text-gray-500 dark:text-gray-400">{day}</div>
                    {entry !== undefined && (
                      <div className={`mt-auto text-xs font-semibold truncate ${cellTextColor(entry.totalGain)}`}>
                        {formatCurrencyCentValue(entry.totalGain, 'USD')}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
